import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Spinner } from 'react-bootstrap';
import { BiArrowToRight } from 'react-icons/bi';
import { platformAPI } from '../../services/platformAPI';
import { buildRoleDashboardFallback } from '../../utils/roleDashboardFallback';
import './RoleDashboard.scss';

function timeGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

const RoleDashboard = ({ variant = 'workspace', showApplications = false, applications = [] }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((s) => s.auth.user);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    return platformAPI
      .roleDashboard()
      .then((res) => setData(res.data?.data || null))
      .catch(async (err) => {
        const status = err.response?.status;
        if (status === 404 || status === 502 || status === 503) {
          const [bootRes, sumRes] = await Promise.all([
            platformAPI.bootstrap(),
            platformAPI.workspaceSummary()
          ]);
          setData(
            buildRoleDashboardFallback(
              bootRes.data?.data,
              sumRes.data?.data,
              user
            )
          );
          return;
        }
        setError('Unable to load your dashboard');
      })
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const go = (route) => {
    if (!route) return;
    const inTender = location.pathname.startsWith('/apps/tender');
    const next = !inTender
      ? route
      : route === '/my-work'
        ? '/apps/tender/my-work'
        : route === '/platform/settings'
          ? '/apps/tender/settings'
          : route === '/profile'
            ? '/apps/tender/profile'
            : route;
    navigate(next);
  };

  const firstName = user?.name?.split(/\s+/)[0] || user?.email?.split('@')[0] || 'there';

  if (loading && !data) {
    return (
      <div className="role-dash role-dash--loading">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="role-dash role-dash--error">
        <p className="text-muted mb-2">{error}</p>
        <Button size="sm" variant="outline-primary" onClick={load}>Retry</Button>
      </div>
    );
  }

  if (!data) return null;

  const heroTitle =
    variant === 'my-work'
      ? 'My work'
      : `${timeGreeting()}, ${firstName}`;
  const mark = String(data.roleLabel || 'MW')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();

  return (
    <div className={`role-dash role-dash--${variant}`}>
      <section className="role-dash__welcome" aria-labelledby="role-dash-title">
        <span className="role-dash__mark" aria-hidden>{mark}</span>
        <div>
          <p>{data.roleLabel}</p>
          <h1 id="role-dash-title">{heroTitle}</h1>
          <small>
            {data.subtitle}
            {data.company?.displayName ? ` · ${data.company.displayName}` : ''}
          </small>
        </div>
        <button type="button" className="role-dash__refresh" onClick={load} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </section>

      <div className="role-dash__tiles">
        {(data.kpis || []).map((kpi) => (
          <article key={kpi.key} className="role-dash__tile">
            <div>
              <strong>{kpi.value}</strong>
              <span>{kpi.label}</span>
              <small>{kpi.hint}</small>
            </div>
            <i aria-hidden>{String(kpi.label || '•').trim().slice(0, 1)}</i>
          </article>
        ))}
      </div>

      <div className="role-dash__columns">
        <section className="role-dash__panel">
          <div className="role-dash__panel-head">
            <h2 className="role-dash__panel-title">Take action now</h2>
            <p className="role-dash__panel-lead">Prioritised items and upcoming deadlines.</p>
          </div>
          {(data.priorities || []).length ? (
            <ul className="role-dash__priority-list">
              {data.priorities.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className="role-dash__priority-row"
                    onClick={() => go(item.routePath)}
                    disabled={!item.routePath}
                  >
                    <div>
                      <div className="role-dash__priority-title">{item.title}</div>
                      {item.description ? (
                        <div className="role-dash__priority-desc">{item.description}</div>
                      ) : null}
                    </div>
                    {item.routePath ? <BiArrowToRight aria-hidden /> : null}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted small mb-0">No open priorities — you are clear to focus on new pursuits.</p>
          )}
        </section>

        <section className="role-dash__panel">
          <div className="role-dash__panel-head">
            <h2 className="role-dash__panel-title">Quick actions</h2>
            <p className="role-dash__panel-lead">Jump straight to the screens you use most in this role.</p>
          </div>
          <div className="role-dash__actions">
            {(data.actions || []).map((action) => (
              <button
                key={action.route + action.label}
                type="button"
                className={`role-dash__action${action.variant === 'primary' ? ' role-dash__action--primary' : ''}`}
                onClick={() => go(action.route)}
              >
                {action.label}
                <BiArrowToRight aria-hidden />
              </button>
            ))}
          </div>
        </section>
      </div>

      {(data.insights || []).length ? (
        <section className="role-dash__panel role-dash__brief">
          <div className="role-dash__panel-head">
            <h2 className="role-dash__panel-title">Brief</h2>
            <p className="role-dash__panel-lead">What is worth a look before you start.</p>
          </div>
          <ul>
            {(data.insights || []).map((row) => (
              <li key={row.title}>
                <strong>{row.title}</strong>
                {row.detail ? <span>{row.detail}</span> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {showApplications && applications.length ? (
        <>
          <div className="ws-section-label">Applications</div>
          <div className="ws-app-grid">
            {applications.map((app) => {
              const active = app.enabled && app.entitled;
              return (
                <article
                  key={app.key}
                  className={`ws-app-card ${!active ? 'ws-app-card--muted' : ''}`}
                >
                  <div className="ws-app-card__head">
                    <h2 className="ws-app-card__title">{app.displayName}</h2>
                  </div>
                  <p className="ws-app-card__desc">{app.description}</p>
                  {active && app.entryRoute ? (
                    <Button size="sm" variant="primary" onClick={() => navigate(app.entryRoute)}>
                      Open
                    </Button>
                  ) : null}
                </article>
              );
            })}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default RoleDashboard;
